import React from 'react';
import { useTranslation } from 'react-i18next';

import { useAppSelector } from '@/store';
import { activeJobs, selectCreativeJobsMap } from '@/store/slices/creativeJobs';

import { Button, Icon, Modal, Text } from '../../../../components';
import type { FtuxTargetId } from '../../../../features/ftux/targets';
import { useReducedMotion } from '../../../../hooks/useReducedMotion';
import { useThemeMode } from '../../../../theme/OwsTheme';
import { CUST_GEN_DOT_FILL, CUST_GEN_DOT_FILL_LIGHT } from '../../../../theme/customizer-tokens';
import { DotField } from '../../../customizer/components/DotField';
import { LumioMorph } from '../../../home/components/BrandDeploymentStrip/LumioMorph';
import { useScrollEdges } from '../../../creatives/hooks/useScrollEdges';
import type { ArtStyleSummary } from '../../art-style-api';
import { lookLabel, type StripEntry } from '../../site-looks';
import { artStyleLabel } from '../../useActiveArtStyle';
import { StyleCardMenu } from './StyleCardMenu';
import {
  Root,
  Scroller,
  Card,
  MenuSlot,
  Select,
  Thumb,
  ThumbImg,
  ThumbEmpty,
  ActiveBadge,
  ActiveDot,
  MatureTag,
  ArtTag,
  Name,
  Untitled,
  NewCard,
  NewPlus,
  NewLabel,
  Spinner,
  ModalCopy,
  EdgeArrow,
  StripFadeProperties,
  BusyOverlay,
  BusyBackdrop,
  BusyMark,
} from './StylesStrip.styles';

export type StylesStripProps = {
  /** The brand's styles and catalog looks in strip order (see `stripEntries`). */
  entries: readonly StripEntry[];
  /** The open card's key (`style:<id>` or `look:<id>`). */
  selectedKey: string | undefined;
  /** A new style or a duplicate is being created (both controls wait for it). */
  creating: boolean;
  onSelect: (entry: StripEntry) => void;
  onCreate: () => void;
  onDuplicate: (styleId: string) => void;
  /** Delete a style; resolves true once it is gone (the confirmation then closes). */
  onDelete: (styleId: string) => Promise<boolean>;
  /** The FTUX lights "New style" (the art style piece's New style step): it holds its hovered look meanwhile. */
  newLit?: boolean;
};

/* Where the FTUX's art style piece lights New style and presses its plus (inert outside an FTUX step). */
const NEW_STYLE_TARGET: FtuxTargetId = 'style-new';
const NEW_STYLE_PLUS_TARGET: FtuxTargetId = 'style-new-plus';

/** A thumbnail that fades in once decoded (a new URL fades in again). */
const CardThumb = ({ url }: { url: string }): React.ReactElement => {
  const [loaded, setLoaded] = React.useState(false);
  const imgRef = React.useRef<HTMLImageElement>(null);
  // A cached image can finish loading before this runs: resetting to "not loaded" then hid it for good (its onLoad
  // had already fired). Read whether it is complete instead, as the sample cards do.
  React.useEffect(() => {
    setLoaded(Boolean(imgRef.current?.complete));
  }, [url]);
  return (
    <ThumbImg
      ref={imgRef}
      src={url}
      alt=""
      loading="lazy"
      decoding="async"
      draggable={false}
      $loaded={loaded}
      onLoad={() => setLoaded(true)}
    />
  );
};

/**
 * The brand's art styles and its catalog looks as one row of cards above the editor: each card shows its thumbnail (a
 * style's newest sample, else its first mood image; a look's cover), its name, a Primary badge on the look the website
 * shows and an "18+" tag on a mature one. A catalog look also says when the brand has no art (or only some) in it, and
 * has no menu (it can't be changed). Picking a card opens it below; "New style" (the row's first card) starts a blank
 * draft, and the primary look comes right after it. A style card's menu duplicates it or deletes it (after a
 * confirmation). The row scrolls sideways once it outgrows the page and keeps the selected card in view.
 */
export const StylesStrip = ({
  entries,
  selectedKey,
  creating,
  onSelect,
  onCreate,
  onDuplicate,
  onDelete,
  newLit = false,
}: StylesStripProps): React.ReactElement => {
  const { t } = useTranslation();
  const reduced = useReducedMotion();
  const scrollerRef = React.useRef<HTMLDivElement>(null);
  const edges = useScrollEdges(scrollerRef, entries.length);
  const items = React.useMemo(
    () => entries.flatMap((entry) => (entry.kind === 'style' ? [entry.style] : [])),
    [entries],
  );

  // Cards shown on first render settle in with the page; only cards that join later animate in.
  const initialIdsRef = React.useRef<Set<string> | null>(null);
  if (initialIdsRef.current === null) initialIdsRef.current = new Set(entries.map((entry) => entry.key));
  const initialIds = initialIdsRef.current;

  const [confirmId, setConfirmId] = React.useState<string | null>(null);
  const [deleting, setDeleting] = React.useState(false);
  /** The card whose menu is open keeps its kebab shown while the pointer is on the menu. */
  const [menuOpenId, setMenuOpenId] = React.useState<string | null>(null);
  const confirming = items.find((item) => item.id === confirmId);

  const labelOf = (item: ArtStyleSummary): string => artStyleLabel(item.name);

  // The styles being drawn right now: any in-flight job a style owns (its samples ride the shared jobs list with the
  // style id as their root), read straight from the jobs, so it holds across a reload.
  const jobsById = useAppSelector(selectCreativeJobsMap);
  const busyIds = React.useMemo(() => {
    const ids = new Set(items.map((item) => item.id));
    const busy = new Set<string>();
    for (const job of activeJobs(Object.values(jobsById))) {
      if (job.root_banner_id && ids.has(job.root_banner_id)) busy.add(job.root_banner_id);
    }
    return busy;
  }, [jobsById, items]);
  const dotFill = useThemeMode() === 'light' ? CUST_GEN_DOT_FILL_LIGHT : CUST_GEN_DOT_FILL;

  /** Move the row about a view's width that way (snapping to a card). */
  const page = (way: -1 | 1): void => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    scroller.scrollBy({ left: way * scroller.clientWidth * 0.8, behavior: reduced ? 'auto' : 'smooth' });
  };

  // Keep the selected card in view (a new style lands at the end of the row, a default may be far along).
  React.useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller || !selectedKey) return;
    const card = Array.from(scroller.querySelectorAll<HTMLElement>('[data-strip-key]')).find(
      (el) => el.dataset.stripKey === selectedKey,
    );
    if (!card) return;
    // Against the scroller's padding box, not its edge: flush with the edge, the first card rested a few pixels into
    // the scroll, so its selection ring was clipped and the start read as scrolled (the fade covered it).
    const style = window.getComputedStyle(scroller);
    const outer = scroller.getBoundingClientRect();
    const left = outer.left + parseFloat(style.paddingLeft);
    const right = outer.right - parseFloat(style.paddingRight);
    const inner = card.getBoundingClientRect();
    if (inner.left >= left && inner.right <= right) return;
    // Bring the card to the start of the row (a snap point), never past the row's end: just bringing its right edge in
    // stopped between snap points and the row snapped back, cutting the card; clamped, the last card rests at the row's
    // end (no arrow).
    const max = scroller.scrollWidth - scroller.clientWidth;
    const target = Math.min(max, Math.max(0, scroller.scrollLeft + inner.left - left));
    scroller.scrollTo({ left: target, behavior: reduced ? 'auto' : 'smooth' });
  }, [selectedKey, entries.length, reduced]);

  const confirmDelete = async (): Promise<void> => {
    if (!confirmId || deleting) return;
    setDeleting(true);
    const gone = await onDelete(confirmId);
    setDeleting(false);
    if (gone) setConfirmId(null);
  };

  const confirmingName = confirming ? labelOf(confirming) || t('Untitled style') : '';

  return (
    <Root aria-label={t('Your art styles')}>
      <StripFadeProperties />
      <Scroller ref={scrollerRef} role="list" $fadeStart={edges.start} $fadeEnd={edges.end}>
        {/* "New style" leads the row, then the primary look and the rest (stripEntries puts the primary first). */}
        <NewCard
          type="button"
          data-ftux={NEW_STYLE_TARGET}
          $lit={newLit}
          disabled={creating}
          aria-busy={creating}
          onClick={onCreate}
        >
          <NewPlus aria-hidden="true" data-ftux={NEW_STYLE_PLUS_TARGET}>
            {creating ? <Spinner /> : <Icon name="plus" size={18} />}
          </NewPlus>
          <NewLabel text="New style" tag="span" />
        </NewCard>
        {entries.map((entry, index) => {
          const selected = entry.key === selectedKey;
          if (entry.kind === 'look') {
            const { look } = entry;
            const label = lookLabel(look.name);
            const cover = look.cover_url || look.thumbnail_url;
            return (
              <Card key={entry.key} role="listitem" data-strip-key={entry.key} $fresh={!initialIds.has(entry.key)}>
                <Select
                  type="button"
                  $selected={selected}
                  aria-current={selected ? 'true' : undefined}
                  onClick={() => onSelect(entry)}
                >
                  <Thumb>
                    {cover ? (
                      <CardThumb url={cover} />
                    ) : (
                      <ThumbEmpty aria-hidden="true">
                        <Icon name="palette" size={22} />
                      </ThumbEmpty>
                    )}
                    {entry.busy ? (
                      <BusyOverlay aria-hidden="true">
                        <BusyBackdrop $solid={!cover} />
                        <DotField seed={index + 1} fill={dotFill} />
                        <BusyMark>
                          <LumioMorph />
                        </BusyMark>
                      </BusyOverlay>
                    ) : null}
                    {entry.primary ? (
                      <ActiveBadge>
                        <ActiveDot aria-hidden="true" />
                        <Text text="Primary" tag="span" />
                      </ActiveBadge>
                    ) : null}
                    {look.mature ? (
                      <MatureTag title={t('Mature content')}>
                        <Text text="18+" tag="span" />
                      </MatureTag>
                    ) : null}
                    {!entry.primary && look.art === 'none' ? (
                      <ArtTag>
                        <Text text="No art yet" tag="span" />
                      </ArtTag>
                    ) : null}
                    {!entry.primary && look.art === 'partial' ? (
                      <ArtTag>
                        <Text text="Partly drawn" tag="span" />
                      </ArtTag>
                    ) : null}
                  </Thumb>
                  {label ? (
                    <Name text={label} raw tag="span" $selected={selected} />
                  ) : (
                    <Untitled text="Untitled look" tag="span" $selected={selected} />
                  )}
                </Select>
              </Card>
            );
          }
          const item = entry.style;
          const label = labelOf(item);
          return (
            <Card key={entry.key} role="listitem" data-strip-key={entry.key} $fresh={!initialIds.has(entry.key)}>
              <Select
                type="button"
                $selected={selected}
                aria-current={selected ? 'true' : undefined}
                onClick={() => onSelect(entry)}
              >
                <Thumb>
                  {item.thumb_url ? (
                    <CardThumb url={item.thumb_url} />
                  ) : (
                    <ThumbEmpty aria-hidden="true">
                      <Icon name="palette" size={22} />
                    </ThumbEmpty>
                  )}
                  {busyIds.has(item.id) ? (
                    <BusyOverlay aria-hidden="true">
                      <BusyBackdrop $solid={!item.thumb_url} />
                      <DotField seed={index + 1} fill={dotFill} />
                      <BusyMark>
                        <LumioMorph />
                      </BusyMark>
                    </BusyOverlay>
                  ) : null}
                  {entry.primary ? (
                    <ActiveBadge>
                      <ActiveDot aria-hidden="true" />
                      <Text text="Primary" tag="span" />
                    </ActiveBadge>
                  ) : null}
                  {item.mature ? (
                    <MatureTag title={t('Mature content')}>
                      <Text text="18+" tag="span" />
                    </MatureTag>
                  ) : null}
                </Thumb>
                {label ? (
                  <Name text={label} raw tag="span" $selected={selected} />
                ) : (
                  <Untitled text="Untitled style" tag="span" $selected={selected} />
                )}
              </Select>
              <MenuSlot $pinned={selected || menuOpenId === item.id}>
                <StyleCardMenu
                  styleName={label || t('Untitled style')}
                  disabled={creating}
                  onOpenChange={(open) => setMenuOpenId(open ? item.id : null)}
                  onDuplicate={() => onDuplicate(item.id)}
                  onDelete={() => setConfirmId(item.id)}
                />
              </MenuSlot>
            </Card>
          );
        })}
      </Scroller>
      <EdgeArrow
        type="button"
        $side="start"
        $shown={edges.start}
        aria-label={t('Previous')}
        aria-hidden={!edges.start || undefined}
        tabIndex={edges.start ? undefined : -1}
        onClick={() => page(-1)}
      >
        <Icon name="arrow-left" size={16} />
      </EdgeArrow>
      <EdgeArrow
        type="button"
        $side="end"
        $shown={edges.end}
        aria-label={t('Next')}
        aria-hidden={!edges.end || undefined}
        tabIndex={edges.end ? undefined : -1}
        onClick={() => page(1)}
      >
        <Icon name="arrow-left" size={16} />
      </EdgeArrow>

      <Modal
        open={confirming !== undefined}
        title={t('Delete this style?')}
        onClose={() => {
          if (!deleting) setConfirmId(null);
        }}
        footer={
          <>
            <Button label={t('Cancel')} variant="outline" disabled={deleting} onClick={() => setConfirmId(null)} />
            <Button label={t('Delete style')} icon="trash" disabled={deleting} onClick={() => void confirmDelete()} />
          </>
        }
      >
        <ModalCopy
          text="{{name}} is deleted for good, with its mood board, definition and samples."
          placeholders={{ name: confirmingName }}
          tag="paragraph"
        />
        {confirming?.status === 'active' ? (
          <ModalCopy
            text="It is the style in use, so new banners and images will no longer follow an art style."
            tag="paragraph"
          />
        ) : null}
      </Modal>
    </Root>
  );
};

export default StylesStrip;
